import * as Yup from 'yup';
import Swal from 'sweetalert2';
import { sectionDisplayNames } from './utils';

const phaseSchema = Yup.object().shape({
  1: Yup.object().shape({
    inspection_date: Yup.date().nullable().typeError('Must be a valid date'),
    landlord_name: Yup.string()
      .trim()
      .max(200)
      .when(['landlord_signature', 'landlord_signature_date'], {
        is: (sig, date) => sig || date,
        then: (schema) => schema.required('Landlord/agent name required when signing'),
      }),
    landlord_signature: Yup.string().trim(),
    landlord_signature_date: Yup.date().nullable().typeError('Must be a valid date'),
  }),

  2: Yup.object().shape({
    a: Yup.object({ tenant_name: Yup.string().trim().max(200), tenant_signature: Yup.string().trim() }),
    b: Yup.object({
      tenant_name: Yup.string().trim().max(200),
      disagreement_reasons: Yup.string().trim().max(1500),
    }),
    c: Yup.object({ landlord_refusal_signature: Yup.string().trim() }),
  }),

  3: Yup.object().shape({
    inspection_date: Yup.date().nullable().typeError('Must be a valid date'),
    landlord_name: Yup.string().trim().max(200),
    landlord_signature: Yup.string().trim(),
    landlord_signature_date: Yup.date().nullable().typeError('Must be a valid date'),
    forwarding_address: Yup.string().trim().max(500),
  }),

  completed_at: Yup.date().nullable(),
});

// Variant 3: Named helper functions (most maintainable long-term)

function hasFilledLandlordInitial(phase) {
  return !!(
    phase?.[1]?.inspection_date &&
    phase?.[1]?.landlord_name?.trim() &&
    (phase?.[1]?.landlord_signature?.trim() || phase?.[1]?.landlord_signature_date)
  );
}

function hasValidTenantAcknowledgment(phase) {
  if (!phase?.[2]) return false;

  return (
    (phase[2].a?.tenant_name?.trim() && phase[2].a?.tenant_signature?.trim()) ||
    (phase[2].b?.tenant_name?.trim() && phase[2].b?.disagreement_reasons?.trim()) ||
    !!phase[2].c?.landlord_refusal_signature?.trim()
  );
}

const isPhaseCompleted = (phase) => {
  if (!phase) return false;
  const joint = hasFilledLandlordInitial(phase) && hasValidTenantAcknowledgment(phase);
  const solo = !!(
    phase?.[3]?.inspection_date &&
    phase?.[3]?.landlord_name?.trim() &&
    (phase?.[3]?.landlord_signature?.trim() || phase?.[3]?.landlord_signature_date) &&
    phase?.[3]?.forwarding_address?.trim()
  );

  return joint || solo;
};

const getCurrentPhase = (values) => {
  if (values.current_phase) {
    return values.current_phase;
  }
  const moveIn = values.move_in;
  const moveOut = values.move_out;
  if (moveOut && (moveOut[1] || moveOut[2] || moveOut[3])) {
    return 3;
  }
  if (moveIn && (moveIn[1] || moveIn[2] || moveIn[3])) {
    return 1;
  }
  return 1;
};

export const moveInOutInspectionSchema = Yup.object().shape({
  property_details: Yup.object()
    .required()
    .shape({
      address: Yup.string().required().trim().min(5).max(500),
      unit_number: Yup.string().trim().max(50),
      landlord: Yup.object().required().shape({
        name: Yup.string().required().trim().min(2).max(200),
      }),
      tenants: Yup.array()
        .of(
          Yup.object().shape({
            name: Yup.string().required().trim().min(2).max(200),
            email: Yup.string().email().trim().max(120),
          })
        )
        .min(1)
        .required(),
    }),

  move_in: phaseSchema
    .test('move-in-complete', 'Move-In must be completed (joint 1+2 or solo 3)', function (value) {
      const currentPhase = getCurrentPhase(this.parent);
      if (currentPhase === 3 || currentPhase === 4) {
        return true;
      }
      if (!value || Object.keys(value).length === 0) return true;
      return isPhaseCompleted(value);
    })
    .required(),

  move_out: phaseSchema.test('move-out-logic', null, function (value, context) {
    const currentPhase = getCurrentPhase(context.parent);
    if (currentPhase === 1 || currentPhase === 2) {
      return true;
    }

    const moveIn = context.parent.move_in;

    if (!moveIn || Object.keys(moveIn).length === 0) {
      return this.createError({ message: 'Move-In must be completed before Move-Out', path: 'move_in' });
    }
    if (!isPhaseCompleted(moveIn)) {
      return this.createError({ message: 'Complete Move-In first', path: 'move_in' });
    }
    if (!value || Object.keys(value).length === 0) {
      return this.createError({ message: 'Move-Out information is required for this phase', path: 'move_out' });
    }

    return isPhaseCompleted(value) || this.createError({ message: 'Complete Move-Out (joint 1+2 or solo 3)' });
  }),

  inspection_items: Yup.array()
    .of(
      Yup.object().shape({
        section: Yup.string()
          .required()
          .oneOf(Object.keys(sectionDisplayNames), 'Invalid section'),

        name: Yup.string().required('Item name is required').trim().min(2).max(200),

        move_in_condition: Yup.object({
          code: Yup.number().nullable().oneOf([1, 2, 3, 4, 5, 6], 'Please select a condition'),
          comments: Yup.string().trim().max(2000),
          attachments: Yup.array()
            .of(Yup.string())
            .max(10, 'Maximum 10 media files allowed')
            .when('code', {
              is: (code) => [2, 3, 4].includes(Number(code)),
              then: (schema) =>
                schema.test(
                  'attachments-required-with-pending',
                  'At least one photo or video is required when condition indicates a problem',
                  function (value) {
                    const { path, options } = this;
                    const pendingMedia = options.context?.pendingMedia || {};

                    const match = path.match(/inspection_items\[(\d+)\]/);
                    const idx = match ? Number(match[1]) : null;

                    if (idx === null) return false;

                    const pendingCount = pendingMedia[`${idx}-move_in`]?.length || 0;
                    const savedCount = value?.length || 0;

                    return pendingCount + savedCount > 0;
                  }
                ),
              otherwise: (schema) => schema.nullable(),
            }),
        }).test(
          'move-in-condition-required',
          'Condition must be selected for move-in',
          function (value, context) {
            const root = context.from?.[context.from.length - 1]?.value || {};
            const currentPhase = getCurrentPhase(root);
            if (currentPhase === 1 || currentPhase === 2) {
              return value?.code != null && [1, 2, 3, 4, 5, 6].includes(value.code);
            }
            return true;
          }
        ),

        move_out_condition: Yup.object({
          code: Yup.number().nullable().oneOf([1, 2, 3, 4, 5, 6], 'Please select a condition'),
          comments: Yup.string().trim().max(2000),
          attachments: Yup.array()
            .of(Yup.string())
            .max(10, 'Maximum 10 media files allowed')
            .when('code', {
              is: (code) => [2, 3, 4].includes(Number(code)),
              then: (schema) =>
                schema.test(
                  'attachments-required-with-pending',
                  'At least one photo or video is required when condition indicates a problem',
                  function (value) {
                    const { path, options } = this;
                    const pendingMedia = options.context?.pendingMedia || {};

                    const match = path.match(/inspection_items\[(\d+)\]/);
                    const idx = match ? Number(match[1]) : null;

                    if (idx === null) return false;

                    const pendingCount = pendingMedia[`${idx}-move_out`]?.length || 0;
                    const savedCount = value?.length || 0;

                    return pendingCount + savedCount > 0;
                  }
                ),
              otherwise: (schema) => schema.nullable(),
            }),
        }).test(
          'move-out-condition-required',
          'Condition must be selected for move-out',
          function (value, context) {
            const root = context.from?.[context.from.length - 1]?.value || {};
            const currentPhase = getCurrentPhase(root);
            if (currentPhase === 3 || currentPhase === 4) {
              return value?.code != null && [1, 2, 3, 4, 5, 6].includes(value.code);
            }
            return true;
          }
        ),
      })
    )
    .min(1, 'At least one inspection item is required')
    .required(),

  keys: Yup.object({
    issued: Yup.object({
      suite: Yup.number().min(0).integer().required(),
      building: Yup.number().min(0).integer().required(),
      entry: Yup.number().min(0).integer().required(),
      garage: Yup.number().min(0).integer().required(),
      mailbox: Yup.number().min(0).integer().required(),
      fob: Yup.number().min(0).integer().required(),
      parking_pass: Yup.number().min(0).integer().required(),
      discount_card: Yup.number().min(0).integer().required(),
    }),
    returned: Yup.object({
      suite: Yup.number().min(0).integer().required(),
      building: Yup.number().min(0).integer().required(),
      entry: Yup.number().min(0).integer().required(),
      garage: Yup.number().min(0).integer().required(),
      mailbox: Yup.number().min(0).integer().required(),
      fob: Yup.number().min(0).integer().required(),
      parking_pass: Yup.number().min(0).integer().required(),
      discount_card: Yup.number().min(0).integer().required(),
    }),
  }),

  move_in_conclusions_recommendations: Yup.string().trim().max(5000),
  move_out_conclusions_recommendations: Yup.string().trim().max(5000),
  current_phase: Yup.number().oneOf([1, 2, 3, 4]).nullable(),
});

/**
 * Helper to make field paths more user-friendly
 */
const prettifyPath = (path) => {
  if (!path) return 'Field';

  const fieldMap = {
    'property_details.address': 'Rental Premises Address',
    'property_details.unit_number': 'Unit / Suite Number',
    'property_details.landlord.name': 'Landlord (Company / Partnership)',
    'property_details.tenants': 'Tenants',
    'property_details.tenants[].name': 'Tenant Name',
    'property_details.tenants[].email': 'Tenant Email',
    'move_in': 'Move-In Inspection',
    'move_out': 'Move-Out Inspection',
    'move_in.inspection_date': 'Move-In Inspection Date',
    'move_in.landlord_agent_name': 'Move-In Landlord Agent Name',
    'move_in.landlord_initial_signature': 'Move-In Landlord Signature',
    'move_in.landlord_initial_date': 'Move-In Landlord Signature Date',
    'move_in.tenant_agree_name': 'Move-In Tenant Agree Name',
    'move_in.tenant_agree_signature': 'Move-In Tenant Agree Signature',
    'move_in.tenant_disagree_name': 'Move-In Tenant Disagree Name',
    'move_in.disagree_reasons': 'Move-In Disagreement Reasons',
    'move_in.landlord_refusal_signature': 'Move-In Landlord Refusal Signature',
    'move_in.solo_inspection_date': 'Move-In Solo Inspection Date',
    'move_in.solo_landlord_agent_name': 'Move-In Solo Landlord Agent Name',
    'move_in.solo_landlord_signature': 'Move-In Solo Landlord Signature',
    'move_in.solo_signature_date': 'Move-In Solo Signature Date',
    'move_in.forwarding_address': 'Move-In Forwarding Address',
    'move_out.inspection_date': 'Move-Out Inspection Date',
    'move_out.landlord_agent_name': 'Move-Out Landlord Agent Name',
    'move_out.landlord_initial_signature': 'Move-Out Landlord Signature',
    'move_out.landlord_initial_date': 'Move-Out Landlord Signature Date',
    'move_out.tenant_agree_name': 'Move-Out Tenant Agree Name',
    'move_out.tenant_agree_signature': 'Move-Out Tenant Agree Signature',
    'move_out.tenant_disagree_name': 'Move-Out Tenant Disagree Name',
    'move_out.disagree_reasons': 'Move-Out Disagreement Reasons',
    'move_out.landlord_refusal_signature': 'Move-Out Landlord Refusal Signature',
    'move_out.solo_inspection_date': 'Move-Out Solo Inspection Date',
    'move_out.solo_landlord_agent_name': 'Move-Out Solo Landlord Agent Name',
    'move_out.solo_landlord_signature': 'Move-Out Solo Landlord Signature',
    'move_out.solo_signature_date': 'Move-Out Solo Signature Date',
    'move_out.forwarding_address': 'Move-Out Forwarding Address',
    'inspection_items': 'Inspection Items',
    'inspection_items[].section': 'Item Section',
    'inspection_items[].name': 'Item Name',
    'inspection_items[].move_in_condition.code': 'Move-In Condition Code',
    'inspection_items[].move_in_condition.comments': 'Move-In Comments',
    'inspection_items[].move_out_condition.code': 'Move-Out Condition Code',
    'inspection_items[].move_out_condition.comments': 'Move-Out Comments',
    'keys.issued.suite': 'Suite Keys Issued',
    'keys.issued.building': 'Building Keys Issued',
    'keys.issued.entry': 'Entry Keys Issued',
    'keys.issued.garage': 'Garage Keys Issued',
    'keys.issued.mailbox': 'Mailbox Keys Issued',
    'keys.issued.fob': 'Fob Keys Issued',
    'keys.returned.suite': 'Suite Keys Returned',
    'keys.returned.building': 'Building Keys Returned',
    'keys.returned.entry': 'Entry Keys Returned',
    'keys.returned.garage': 'Garage Keys Returned',
    'keys.returned.mailbox': 'Mailbox Keys Returned',
    'keys.returned.fob': 'Fob Keys Returned',
    'keys.returned.parking_pass': 'Parking Pass Keys Returned',
    'keys.returned.discount_card': 'Discount Card Keys Returned',
    'move_in_conclusions_recommendations': 'Move-In Conclusions / Recommendations',
    'move_out_conclusions_recommendations': 'Move-Out Conclusions / Recommendations',
  };

  return fieldMap[path] || path
    .split('.')
    .map((word, idx) => idx === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' > ')
    .replace(/_/g, ' ');
};

/**
 * Group errors into logical sections for better UX
 */
const groupErrors = (errors, values) => {
  const grouped = {
    'Property & Parties': [],
    'Move-In Inspection': [],
    'Move-Out Inspection': [],
    'Inspection Items': [],
    'Keys Issued / Returned': [],
    'Conclusions & Notes': [],
    'Other': [],
  };

  // ────────────────────────────────────────────────
  // 1. Collect missing move-in conditions BY SECTION
  // ────────────────────────────────────────────────
  const missingMoveInBySection = {};
  const missingMoveOutBySection = {};

  errors.forEach((err) => {
    const { path, message } = err;
    if (
      message.includes('Condition must be selected for move-in') ||
      (path?.includes('move_in_condition.code') && !err.value?.code)
    ) {
      const match = path?.match(/inspection_items\[(\d+)\]/);
      if (match) {
        const idx = parseInt(match[1], 10);
        const item = values?.inspection_items?.[idx];
        if (item?.section && item?.name?.trim()) {
          const sectionKey = item.section;
          if (!missingMoveInBySection[sectionKey]) {
            missingMoveInBySection[sectionKey] = [];
          }
          missingMoveInBySection[sectionKey].push(item.name.trim());
        }
      }
      return;
    }

    if (
      message.includes('Condition must be selected for move-out') ||
      (path?.includes('move_out_condition.code') && !err.value?.code)
    ) {
      const match = path?.match(/inspection_items\[(\d+)\]/);
      if (match) {
        const idx = parseInt(match[1], 10);
        const item = values?.inspection_items?.[idx];
        if (item?.section && item?.name?.trim()) {
          const sectionKey = item.section;
          if (!missingMoveOutBySection[sectionKey]) {
            missingMoveOutBySection[sectionKey] = [];
          }
          missingMoveOutBySection[sectionKey].push(item.name.trim());
        }
      }
      return;
    }

    let cleanMessage = message
      .replace('is required', 'is required')
      .replace('Valid date required', 'must be a valid date')
      .replace(/oneOf.*$/, 'please select a valid option')
      .replace('Maximum 10 pictures allowed', 'max 10 photos allowed');

    const label = prettifyPath(path);

    if (path?.includes('property_details')) {
      grouped['Property & Parties'].push({ label, message: cleanMessage });
    } else if (path?.includes('move_in') || path === 'move_in') {
      grouped['Move-In Inspection'].push({ label, message: cleanMessage });
    } else if (path?.includes('move_out') || path === 'move_out') {
      grouped['Move-Out Inspection'].push({ label, message: cleanMessage });
    } else if (path?.includes('inspection_items') &&
      !path.includes('move_in_condition') &&
      !path.includes('move_out_condition')) {
      grouped['Inspection Items'].push({ label, message: cleanMessage });
    } else if (path?.includes('keys')) {
      grouped['Keys Issued / Returned'].push({ label, message: cleanMessage });
    } else if (['conclusions_recommendations'].includes(path)) {
      grouped['Conclusions'].push({ label, message: cleanMessage });
    } else {
      grouped.Other.push({ label, message: cleanMessage });
    }
  });

  // ────────────────────────────────────────────────
  // 2. Add aggregated missing MOVE-IN conditions
  // ────────────────────────────────────────────────
  const totalMissingMoveIn = Object.values(missingMoveInBySection).reduce((sum, arr) => sum + arr.length, 0);

  if (totalMissingMoveIn > 0) {
    let sectionSummary = '';

    const sectionEntries = Object.entries(missingMoveInBySection);
    const shownSections = sectionEntries.slice(0, 4);

    shownSections.forEach(([sectionKey, items]) => {
      const displayName = sectionDisplayNames[sectionKey] || sectionKey.replace(/_/g, ' ');
      sectionSummary += `<li><strong>${displayName}</strong>: ${items.length} item${items.length === 1 ? '' : 's'}</li>`;
    });

    if (sectionEntries.length > 4) {
      sectionSummary += `<li><em>and ${sectionEntries.length - 4} more sections…</em></li>`;
    }

    grouped['Move-In Inspection'].unshift({
      label: 'Missing Move-In Conditions',
      message: `
        <div>
          <strong>${totalMissingMoveIn} inspection item${totalMissingMoveIn === 1 ? '' : 's'} still need a condition selected.</strong><br>
          Please review:
          <ul style="margin:8px 0 8px 24px; padding-left:0; list-style-type:disc;">
            ${sectionSummary}
          </ul>
          <small>(each item must have: Clean/OK, Needs Cleaning, Needs Painting, Needs Repair, Missing or Not Applicable)</small>
        </div>
      `,
    });
  }

  // ────────────────────────────────────────────────
  // 3. Add aggregated missing MOVE-OUT conditions
  // ────────────────────────────────────────────────
  const totalMissingMoveOut = Object.values(missingMoveOutBySection).reduce((sum, arr) => sum + arr.length, 0);

  if (totalMissingMoveOut > 0) {
    let sectionSummary = '';

    const sectionEntries = Object.entries(missingMoveOutBySection);
    const shownSections = sectionEntries.slice(0, 4);

    shownSections.forEach(([sectionKey, items]) => {
      const displayName = sectionDisplayNames[sectionKey] || sectionKey.replace(/_/g, ' ');
      sectionSummary += `<li><strong>${displayName}</strong>: ${items.length} item${items.length === 1 ? '' : 's'}</li>`;
    });

    if (sectionEntries.length > 4) {
      sectionSummary += `<li><em>and ${sectionEntries.length - 4} more sections…</em></li>`;
    }

    grouped['Move-Out Inspection'].unshift({
      label: 'Missing Move-Out Conditions',
      message: `
        <div>
          <strong>${totalMissingMoveOut} inspection item${totalMissingMoveOut === 1 ? '' : 's'} still need a condition selected.</strong><br>
          Please review:
          <ul style="margin:8px 0 8px 24px; padding-left:0; list-style-type:disc;">
            ${sectionSummary}
          </ul>
          <small>(each item must have: Clean/OK, Needs Cleaning, Needs Painting, Needs Repair, or Missing)</small>
        </div>
      `,
    });
  }

  Object.keys(grouped).forEach(key => {
    if (grouped[key].length === 0) delete grouped[key];
  });

  return grouped;
};

export const validateInspectionStep = async (
  schema,
  values,
  userRole = 'manager',
  extraContext = {}
) => {
  const dynamicSchema = typeof schema === 'function' ? schema(userRole) : schema;

  try {
    await dynamicSchema.validate(values, {
      abortEarly: false,
      context: extraContext,
    });
    return true;
  } catch (err) {
    if (!err.inner) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation Error ⚠️',
        text: err.message,
        confirmButtonColor: '#34c38f',
      });
      return false;
    }

    const errors = err.inner.map((e) => ({
      path: e.path || '',
      message: e.message,
    }));

    const groupedErrors = groupErrors(errors, values);

    let html = `
  <p style="margin-bottom: 18px; font-size: 16px; color: #333;">
    Please correct the following issues before continuing:
  </p>
  <div style="text-align: left; padding: 20px; background:#fff9e6; border:2px solid #ffeb3b; border-radius:12px; max-height:480px; overflow-y:auto;">
`;

    Object.entries(groupedErrors).forEach(([section, list]) => {
      if (list.length > 0) {
        html += `
      <h4 style="margin:16px 0 10px; font-size:15px; color:#b8860b; border-bottom:1px solid #eee; padding-bottom:4px;">
        ${section}
      </h4>
    `;
        list.forEach(({ label, message }, idx) => {
          const isAggregate = message.includes('<div') || message.includes('<ul');
          html += `
        <div style="margin-bottom:12px; color:#444;">
          ${isAggregate ? '' : `<strong>${idx + 1}. ${label}:</strong> `}
          ${message}
        </div>
      `;
        });
      }
    });

    html += `</div>`;

    await Swal.fire({
      icon: 'warning',
      title: 'Missing or Invalid Fields ⚠️',
      html,
      width: 800,
      confirmButtonText: "Okay, I'll Fix Them",
      confirmButtonColor: '#34c38f',
    });

    return false;
  }
};

export default moveInOutInspectionSchema;